import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { sprintsApi } from '@/services/api/sprints';
import { handleSprintEdgeCases } from '@/utils/sprintUtils';
import type { Sprint } from '@/types/sprint';
import type { ParsedSprintRow } from '@/utils/csvParsing';

const SPRINTS_QUERY_KEY = ['sprints'] as const;

function rowToSprint(row: {
  id: number;
  user_id: string;
  sprint_number: number;
  name: string;
  start_date: string;
  end_date: string;
}): Sprint {
  return {
    id: row.id,
    user_id: row.user_id,
    sprint_number: row.sprint_number,
    name: row.name,
    start_date: new Date(`${row.start_date}T00:00:00`),
    end_date: new Date(`${row.end_date}T00:00:00`),
  };
}

/**
 * Returns sprints for the current user from Supabase.
 * Returns an empty array while unauthenticated or loading.
 */
export function useSprints(): Sprint[] {
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: SPRINTS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await sprintsApi.getAll();
      if (error) throw error;
      return (data ?? []).map(rowToSprint);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  return data ?? [];
}

/**
 * Mutation hook to replace all sprints for the current user with an imported set.
 * Invalidates the sprints query on success so timelines update immediately.
 */
export function useImportSprints() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (sprints: ParsedSprintRow[]) => {
      if (!user) throw new Error('Must be authenticated to import sprints');
      const { data, error } = await sprintsApi.importSprints(sprints, user.id);
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: SPRINTS_QUERY_KEY });
      if (data) {
        handleSprintEdgeCases(data.map(rowToSprint));
      }
    },
    onError: () => {
      // Delete may have committed before insert failed; invalidate to keep UI in sync with DB
      queryClient.invalidateQueries({ queryKey: SPRINTS_QUERY_KEY });
    },
  });
}
